import React, {useState, useRef, useEffect} from 'react';
import {Box, Stack} from '@mui/material';
import useClickOutside from '@/hooks/use-click-outside';

type InteractionMode = 'click' | 'hover';

const DEFAULTS = {
  RADIUS: 120,
  DURATION: 300,
  STAGGER: 50,
  OFFSET_ROTATION: 0,
  INTERACTION_MODE: 'click' as InteractionMode,
  HOVER_ON_CLOSE_DELAY: 300,
};

export interface RadialMenuItem {
  id: string;
  content: React.ReactNode | ((isSelected: boolean) => React.ReactNode);
  angle?: number;
  style?: React.CSSProperties;
  activeStyle?: React.CSSProperties;
}

export interface RadialMenuProps {
  trigger: React.ReactNode | ((isOpen: boolean) => React.ReactNode);
  items: RadialMenuItem[];
  selectedIds: string[];
  onOptionClick?: (id: string) => void;
  radius?: number;
  duration?: number;
  stagger?: number;
  offsetRotation?: number;
  interactionMode?: InteractionMode;
  hoverCloseDelay?: number;
}

export default function RadialMenu({
  trigger,
  items,
  selectedIds,
  onOptionClick,
  radius = DEFAULTS.RADIUS,
  duration = DEFAULTS.DURATION,
  stagger = DEFAULTS.STAGGER,
  offsetRotation = DEFAULTS.OFFSET_ROTATION,
  interactionMode = DEFAULTS.INTERACTION_MODE,
  hoverCloseDelay = DEFAULTS.HOVER_ON_CLOSE_DELAY,
}: RadialMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useClickOutside(() => {
    if (interactionMode === 'click') {
      setIsOpen(false);
    }
  });

  const handleMouseEnter = () => {
    if (interactionMode === 'hover') {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      setIsOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (interactionMode === 'hover') {
      closeTimerRef.current = setTimeout(() => {
        setIsOpen(false);
      }, hoverCloseDelay);
    }
  };

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  const toRad = (deg: number) => (deg * Math.PI) / 180;

  const getPosition = (index: number, angleOverride?: number) => {
    if (!isOpen) return {x: 0, y: 0, opacity: 0, scale: 0};

    const step = 360 / items.length;
    const angleInDegrees =
      angleOverride !== undefined ? angleOverride : index * step;
    const angleInRad = toRad(angleInDegrees + offsetRotation);

    return {
      x: Math.round(radius * Math.cos(angleInRad)),
      y: Math.round(radius * Math.sin(angleInRad)),
      opacity: 1,
      scale: 1,
    };
  };

  const selectedIdSet = new Set(selectedIds);

  return (
    <Stack
      ref={wrapperRef}
      className="radial-menu-wrapper"
      data-testid="radial-menu-wrapper"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      direction="row"
      justifyContent="center"
      alignItems="center"
      sx={{position: 'relative', width: 'fit-content'}}
    >
      {items.map((item, index) => {
        const pos = getPosition(index, item.angle);
        const isSelected = selectedIdSet.has(item.id);
        const itemStyle = {
          ...(isSelected && item.activeStyle ? item.activeStyle : {}),
          ...item.style,
        };

        return (
          <Box
            key={item.id}
            data-testid={`radial-menu-item-${item.id}`}
            data-selected={isSelected}
            onClick={(e) => {
              e.stopPropagation();
              onOptionClick?.(item.id);
              if (interactionMode === 'click') {
                setIsOpen(false);
              }
            }}
            style={{
              position: 'absolute',
              transform: `translate(${pos.x}px, ${pos.y}px) scale(${pos.scale})`,
              opacity: pos.opacity,
              zIndex: isOpen ? 5 : 0,
              cursor: 'pointer',
              pointerEvents: isOpen ? 'auto' : 'none',
              transition: `all ${duration}ms cubic-bezier(0.175, 0.885, 0.32, 1.275)`,
              transitionDelay: isOpen ? `${index * stagger}ms` : '0ms',
              ...itemStyle,
            }}
          >
            {typeof item.content === 'function'
              ? item.content(isSelected)
              : item.content}
          </Box>
        );
      })}

      <Box
        data-testid="radial-menu-trigger"
        onClick={() => {
          if (interactionMode === 'click') {
            setIsOpen(!isOpen);
          }
        }}
        sx={{zIndex: 10, cursor: 'pointer', position: 'relative'}}
      >
        {typeof trigger === 'function' ? trigger(isOpen) : trigger}
      </Box>
    </Stack>
  );
}
